import React, { useEffect, useState, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, CartesianGrid, Area, AreaChart,
  PieChart, Pie, Cell, RadialBarChart, RadialBar
} from 'recharts';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useFormatMoneda } from '../utils/moneda';

const ZONA_COLORS = ['#38bdf8', '#818cf8', '#34d399', '#fbbf24', '#f87171', '#a78bfa'];
const ESTADO_COLORS = { activo: '#38bdf8', cancelado: '#34d399', vencido: '#f87171', refinanciado: '#fbbf24' };

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${color}` }}>
      <p style={S.cardLabel}>{label}</p>
      <p style={{ ...S.cardValue, color }}>{value}</p>
      {sub && <p style={S.cardSub}>{sub}</p>}
    </div>
  );
}

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function Calendario({ cobrosMap, vencidasMap, porVencerMap, year, month, onNavegar }) {
  const formatMoneda = useFormatMoneda();
  const primerDia = new Date(year, month - 1, 1).getDay();
  const diasEnMes = new Date(year, month, 0).getDate();
  const hoy = new Date();
  const esHoy = (d) => d === hoy.getDate() && month === hoy.getMonth() + 1 && year === hoy.getFullYear();

  const celdas = [];
  for (let i = 0; i < primerDia; i++) celdas.push(null);
  for (let d = 1; d <= diasEnMes; d++) celdas.push(d);

  function key(d) {
    return `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  }

  return (
    <div style={S.calWrap}>
      <div style={S.calHeader}>
        <button style={S.calNavBtn} onClick={() => onNavegar(-1)}>‹</button>
        <span style={S.calTitle}>{MESES[month - 1]} {year}</span>
        <button style={S.calNavBtn} onClick={() => onNavegar(1)}>›</button>
      </div>
      <div style={S.calGrid7}>
        {DIAS.map(d => <div key={d} style={S.calDayName}>{d}</div>)}
        {celdas.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const k = key(d);
          const cobro    = cobrosMap[k];
          const vencida  = vencidasMap[k];
          const porVencer = porVencerMap[k];
          return (
            <div key={d} style={{ ...S.calCell, ...(esHoy(d) ? S.calHoy : {}) }}>
              <span style={{ ...S.calNum, ...(esHoy(d) ? { color: '#ffffff' } : {}) }}>{d}</span>
              <div style={S.calDots}>
                {cobro    && <span style={{ ...S.dot, background: '#34d399' }} title={`${cobro.cantidad} cobro(s) - ${formatMoneda(cobro.total)}`} />}
                {vencida  && <span style={{ ...S.dot, background: '#f87171' }} title={`${vencida.cantidad} vencida(s)`} />}
                {porVencer && <span style={{ ...S.dot, background: '#fbbf24' }} title={`${porVencer.cantidad} por vencer`} />}
              </div>
            </div>
          );
        })}
      </div>
      <div style={S.calLeyenda}>
        <span><span style={{ ...S.dot, background: '#34d399' }} /> Cobros</span>
        <span><span style={{ ...S.dot, background: '#f87171' }} /> Vencidas</span>
        <span><span style={{ ...S.dot, background: '#fbbf24' }} /> Por vencer</span>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label, prefix = '$' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
      <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 12 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '2px 0 0', color: p.color || '#f1f5f9', fontWeight: 700, fontSize: 13 }}>
          {p.name}: {prefix}{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const formatMoneda = useFormatMoneda();
  const [resumen, setResumen] = useState(null);
  const [calendario, setCalendario] = useState(null);
  const [calYear,  setCalYear]  = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);

  const cargarCalendario = useCallback((y, m) => {
    api.get(`/reportes/calendario?year=${y}&month=${m}`)
      .then(r => setCalendario(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/reportes/resumen')
      .then(r => setResumen(r.data))
      .catch(err => toast.error(err.response?.data?.error || 'Error cargando resumen'));
  }, []);

  useEffect(() => {
    cargarCalendario(calYear, calMonth);
  }, [calYear, calMonth, cargarCalendario]);

  function navegarMes(dir) {
    let m = calMonth + dir, y = calYear;
    if (m > 12) { m = 1;  y++; }
    if (m < 1)  { m = 12; y--; }
    setCalMonth(m); setCalYear(y);
  }

  if (!resumen) return <p style={{ color: 'var(--text-2)' }}>Cargando...</p>;

  // Mapas para el calendario
  const cobrosMap    = Object.fromEntries((calendario?.cobros    || []).map(c => [c.fecha, c]));
  const vencidasMap  = Object.fromEntries((calendario?.vencidas  || []).map(c => [c.fecha, c]));
  const porVencerMap = Object.fromEntries((calendario?.porVencer || []).map(c => [c.fecha, c]));

  // Datos gráfico de tendencia (últimos 30 días)
  const tendencia = (calendario?.tendenciaCobros || []).map(c => ({
    fecha: c.fecha.slice(5),
    total: parseFloat(c.total.toFixed(0))
  }));

  // Datos pie (cartera por zona)
  const porZona = (calendario?.cartPorZona || []).map(z => ({
    name: z.zona,
    value: parseFloat(z.cartera.toFixed(0))
  }));

  // Datos radial (por estado de crédito)
  const porEstado = (calendario?.cartPorEstado || []).map(e => ({
    name: e.estado,
    value: e.cantidad,
    fill: ESTADO_COLORS[e.estado] || '#94a3b8'
  }));

  // Datos barras comparativas
  const barData = [
    { name: 'Cartera Total',   valor: resumen.total_cartera },
    { name: 'Cobrado Mes',     valor: resumen.total_cobrado_mes },
    { name: 'Saldo Pendiente', valor: resumen.saldo_pendiente },
    { name: 'Cobrado Hoy',     valor: resumen.total_cobrado_hoy },
  ];

  return (
    <div>
      <h2 style={S.title}>Dashboard</h2>

      {/* Stat cards */}
      <div style={S.statsGrid}>
        <StatCard label="Clientes activos"     value={resumen.total_clientes}                               color="#38bdf8" />
        <StatCard label="Créditos activos"     value={resumen.total_creditos_activos}                       color="#818cf8" />
        <StatCard label="Cartera total"        value={formatMoneda(resumen.total_cartera)}                 color="#34d399" />
        <StatCard label="Cobrado hoy"          value={formatMoneda(resumen.total_cobrado_hoy)}             color="#fbbf24" />
        <StatCard label="Cobrado este mes"     value={formatMoneda(resumen.total_cobrado_mes)}             color="#a78bfa" />
        <StatCard label="Cuotas vencidas"      value={resumen.cuotas_vencidas}                              color="#f87171" sub="Requieren atención" />
        <StatCard label="Saldo pendiente"      value={formatMoneda(resumen.saldo_pendiente)}               color="#fb923c" />
      </div>

      {/* Fila 2: Barras + Calendario */}
      <div style={S.row2}>
        <div style={{ ...S.chartBox, flex: 1 }}>
          <h3 style={S.chartTitle}>Resumen financiero</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                {barData.map((_, i) => (
                  <Cell key={i} fill={['#38bdf8','#818cf8','#fb923c','#34d399'][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...S.chartBox, minWidth: '300px' }}>
          <Calendario
            cobrosMap={cobrosMap}
            vencidasMap={vencidasMap}
            porVencerMap={porVencerMap}
            year={calYear}
            month={calMonth}
            onNavegar={navegarMes}
          />
        </div>
      </div>

      {/* Fila 3: Tendencia + Pie por zona */}
      <div style={S.row2}>
        <div style={{ ...S.chartBox, flex: 2 }}>
          <h3 style={S.chartTitle}>Tendencia de cobros (últimos 30 días)</h3>
          {tendencia.length === 0
            ? <p style={S.noData}>Sin datos de cobros recientes</p>
            : <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={tendencia} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="gradCobros" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#38bdf8" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#38bdf8" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="fecha" stroke="#64748b" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="total" name="Cobrado" stroke="#38bdf8" strokeWidth={2} fill="url(#gradCobros)" />
                </AreaChart>
              </ResponsiveContainer>
          }
        </div>

        <div style={{ ...S.chartBox, flex: 1, minWidth: '240px' }}>
          <h3 style={S.chartTitle}>Cartera por zona</h3>
          {porZona.length === 0
            ? <p style={S.noData}>Sin datos</p>
            : <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={porZona} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                      dataKey="value" paddingAngle={3}>
                      {porZona.map((_, i) => <Cell key={i} fill={ZONA_COLORS[i % ZONA_COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={S.pieLeyenda}>
                  {porZona.map((z, i) => (
                    <span key={z.name} style={S.pieLeyItem}>
                      <span style={{ ...S.dot, background: ZONA_COLORS[i % ZONA_COLORS.length] }} />
                      {z.name}
                    </span>
                  ))}
                </div>
              </>
          }
        </div>
      </div>

      {/* Fila 4: Radial estado + Line cobros vs vencidos */}
      <div style={S.row2}>
        <div style={{ ...S.chartBox, flex: 1, minWidth: '240px' }}>
          <h3 style={S.chartTitle}>Créditos por estado</h3>
          {porEstado.length === 0
            ? <p style={S.noData}>Sin datos</p>
            : <>
                <ResponsiveContainer width="100%" height={200}>
                  <RadialBarChart cx="50%" cy="50%" innerRadius={30} outerRadius={90}
                    data={porEstado} startAngle={180} endAngle={-180}>
                    <RadialBar dataKey="value" cornerRadius={6} label={{ position: 'insideStart', fill: '#fff', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip prefix="" />} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div style={S.pieLeyenda}>
                  {porEstado.map(e => (
                    <span key={e.name} style={S.pieLeyItem}>
                      <span style={{ ...S.dot, background: e.fill }} />
                      {e.name} ({e.value})
                    </span>
                  ))}
                </div>
              </>
          }
        </div>

        <div style={{ ...S.chartBox, flex: 2 }}>
          <h3 style={S.chartTitle}>Cobros vs cuotas vencidas este mes</h3>
          {(() => {
            const cobrosPorDia   = cobrosMap;
            const vencidasPorDia = vencidasMap;
            const dias = new Date(calYear, calMonth, 0).getDate();
            const data = Array.from({ length: dias }, (_, i) => {
              const k = `${calYear}-${String(calMonth).padStart(2,'0')}-${String(i+1).padStart(2,'0')}`;
              return {
                dia: i + 1,
                cobros:   cobrosPorDia[k]?.cantidad   || 0,
                vencidas: vencidasPorDia[k]?.cantidad || 0,
              };
            }).filter(d => d.cobros > 0 || d.vencidas > 0);

            if (data.length === 0) return <p style={S.noData}>Sin actividad en {MESES[calMonth-1]}</p>;

            return (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="dia" stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip prefix="" />} />
                  <Legend wrapperStyle={{ color: 'var(--text-2)', fontSize: 12 }} />
                  <Line type="monotone" dataKey="cobros"   name="Cobros"   stroke="#34d399" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="vencidas" name="Vencidas" stroke="#f87171" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} strokeDasharray="5 3" />
                </LineChart>
              </ResponsiveContainer>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

const S = {
  title:      { margin: '0 0 1.25rem', color: 'var(--text-1)', fontSize: '1.5rem' },
  statsGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: '0.85rem', marginBottom: '1.25rem' },
  card:       { background: 'var(--bg-card)', borderRadius: '10px', padding: '1rem 1.25rem' },
  cardLabel:  { margin: 0, color: 'var(--text-2)', fontSize: '0.82rem' },
  cardValue:  { margin: '0.35rem 0 0', fontSize: '1.5rem', fontWeight: 700 },
  cardSub:    { margin: '0.2rem 0 0', color: 'var(--text-3)', fontSize: '0.75rem' },
  row2:       { display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' },
  chartBox:   { background: 'var(--bg-card)', borderRadius: '10px', padding: '1.25rem', minWidth: 0 },
  chartTitle: { margin: '0 0 0.85rem', color: 'var(--text-2)', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  noData:     { textAlign: 'center', color: 'var(--text-4)', padding: '2rem 0', fontSize: '0.9rem' },
  dot:        { display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', marginRight: '5px', flexShrink: 0 },
  pieLeyenda: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center', marginTop: '0.5rem' },
  pieLeyItem: { display: 'flex', alignItems: 'center', color: 'var(--text-2)', fontSize: '0.8rem' },
  // Calendario
  calWrap:    { },
  calHeader:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' },
  calTitle:   { color: 'var(--text-1)', fontWeight: 700, fontSize: '0.95rem' },
  calNavBtn:  { background: 'var(--bg-hover)', border: 'none', color: 'var(--text-1)', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 },
  calGrid7:   { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' },
  calDayName: { color: 'var(--text-3)', fontSize: '0.7rem', textAlign: 'center', padding: '4px 0', fontWeight: 600 },
  calCell:    { background: 'var(--bg-app)', borderRadius: '6px', padding: '4px 3px', minHeight: '44px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  calHoy:     { background: '#2563eb', outline: '1.5px solid #38bdf8' },
  calNum:     { color: 'var(--text-1s)', fontSize: '0.78rem', fontWeight: 600, lineHeight: 1 },
  calDots:    { display: 'flex', gap: '2px', marginTop: '4px', flexWrap: 'wrap', justifyContent: 'center' },
  calLeyenda: { display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-2)' },
};
